import React, { useMemo } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Box, Typography, CircularProgress } from '@mui/material';
import * as types from 'application/_types/types.type';
import ToggleSwitch from 'application/components/ToggleSwitch/ToggleSwitch';
import FormSection from 'application/components/FormSection/FormSection';
import { IconMenuItem, IconSelect } from '../styledComponents/IconSelect';
import { getPinsDict } from 'application/routes/config/utils/tempsUtils';
import { GamifyIcon } from 'application/components/GamifyIcon';
import {
  StyledTextField,
  FieldLabel,
  SectionTitle,
  SwitchLabel,
  SwitchDescription,
  StyledButton,
  SecondaryButton,
} from './styledFormComponents';

const pinItems = getPinsDict();

interface StageSettingsFormProps {
  stage: types.StagePartial & { isDraft?: boolean }; // CHANGED: allow isDraft flag
  selectedCRM: types.StageCategoryPartial;
  onSubmit: (values: types.StagePartial) => void;
  onDelete: () => void;
}

export default function StageSettingsForm({
  stage,
  onSubmit,
  onDelete,
  selectedCRM,
}: StageSettingsFormProps) {
  const initialValues: types.StagePartial = {
    id: stage.id || '', // may be temp id for drafts
    isActive: stage.isActive || false,
    isDisplay: stage.isDisplay || false,
    requireCloser: stage.requireCloser || false,
    sendToCRM: stage.sendToCRM || false,
    userSelectable: stage.userSelectable || false,
    title: stage.title || '', // drafts default to "New Stage"
    description: stage.description || '',
    isMapDefault: stage.isMapDefault || false,
    changeToDispositionID: stage.changeToDispositionID ?? '',
    // NOTE: if your StagePartial includes 'sequence', it will pass through from parent; not required here
  };

  const dispById = Object.fromEntries(
    (selectedCRM?.dispositions ?? []).map((d) => [d.id, d])
  );

  const existingTitles = useMemo(
    () =>
      (selectedCRM?.stages ?? []).map((s) => ({
        id: s.id,
        title: (s.title || '').trim().toLowerCase(),
      })),
    [selectedCRM]
  );

  const ValidationSchema = useMemo(
    () =>
      Yup.object({
        title: Yup.string()
          .transform((v) => (v ?? '').trim())
          .required('Title is required')
          .max(40, 'Must be 40 characters or less')
          .test(
            'unique-title-in-category',
            'A stage with this title already exists in this category',
            function (value) {
              if (!value) return true;
              const normalized = value.trim().toLowerCase();
              const conflict = existingTitles.some(
                (t) => t.title === normalized && t.id !== stage.id
              );
              return !conflict;
            }
          ),
        // Keeping your existing rule; if you later add a stage icon field, this stays valid.
        changeToDispositionID: Yup.string().nullable(),
      }),
    [existingTitles, stage.id]
  );

  const handleSubmit = (values: types.StagePartial) => {
    console.log('submitting', values);
    onSubmit({ ...values, title: (values.title || '').trim() });
  };

  return (
    <Box>
      <Formik
        initialValues={initialValues}
        validationSchema={ValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form>
            <FormSection
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <SectionTitle>
                {stage.isDraft || !stage.id
                  ? 'Create new Stage'
                  : 'Stage Settings'}{' '}
                {/* CHANGED */}
              </SectionTitle>

              {stage.isDraft && (
                <Typography
                  sx={(theme) => ({
                    fontSize: 12,
                    color: theme.custom?.textLight,
                  })}
                >
                  This is a new (unsaved) stage. It will be created when you
                  save.
                </Typography>
              )}

              <Box>
                <FieldLabel>Title *</FieldLabel>
                <Field
                  as={StyledTextField}
                  fullWidth
                  name="title"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.title && !!errors.title}
                  helperText={touched.title && (errors.title as any)}
                  inputProps={{ maxLength: 40 }}
                />
              </Box>

              <Box>
                <FieldLabel>Description</FieldLabel>
                <Field
                  as={StyledTextField}
                  fullWidth
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  multiline
                  rows={3}
                />
              </Box>

              <FieldLabel>Advanced options</FieldLabel>

              <FormSection
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ maxWidth: '80%' }}>
                  <SwitchLabel>Active Stage</SwitchLabel>
                  <SwitchDescription>
                    Marks this stage as an active deal that’s still in progress
                    and not yet closed
                  </SwitchDescription>
                </Box>
                <ToggleSwitch
                  checked={Boolean(values.isActive)}
                  onChange={(checked: boolean) =>
                    setFieldValue('isActive', checked, true)
                  }
                />
              </FormSection>

              <FormSection
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ maxWidth: '80%' }}>
                  <SwitchLabel>Display in pipeline</SwitchLabel>
                  <SwitchDescription>
                    Deals in this stage will appear on the pipeline screen
                  </SwitchDescription>
                </Box>
                <ToggleSwitch
                  checked={Boolean(values.isDisplay)}
                  onChange={(checked: boolean) =>
                    setFieldValue('isDisplay', checked, true)
                  }
                />
              </FormSection>

              <FormSection
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ maxWidth: '80%' }}>
                  <SwitchLabel>Send to CRM</SwitchLabel>
                  <SwitchDescription>
                    The stage will be sent to CRM
                  </SwitchDescription>
                </Box>
                <ToggleSwitch
                  checked={Boolean(values.sendToCRM)}
                  onChange={(checked: boolean) =>
                    setFieldValue('sendToCRM', checked, true)
                  }
                />
              </FormSection>

              <FormSection
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ maxWidth: '80%' }}>
                  <SwitchLabel>Require Closer</SwitchLabel>
                  <SwitchDescription>
                    A closer assigned to deal is required for this stage to be
                    set
                  </SwitchDescription>
                </Box>
                <ToggleSwitch
                  checked={Boolean(values.requireCloser)}
                  onChange={(checked: boolean) =>
                    setFieldValue('requireCloser', checked, true)
                  }
                />
              </FormSection>
            </FormSection>

            <FormSection
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                marginTop: 2,
              }}
            >
              <SectionTitle>Stage Disposition</SectionTitle>
              <Box>
                <FieldLabel>Change to disposition</FieldLabel>
              </Box>

              <IconSelect
                name="iconName"
                value={values.changeToDispositionID}
                onChange={(e) =>
                  setFieldValue('changeToDispositionID', e.target.value)
                }
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: (theme) => ({
                      maxHeight: 240,
                      p: 0,
                      backgroundColor: theme.custom?.background1,
                      '& .MuiList-root': { p: 0 },
                    }),
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography
                        sx={(theme) => ({ color: theme.custom?.textLight })}
                      >
                        Select a disposition...
                      </Typography>
                    );
                  }
                  const item = dispById[selected as string];
                  if (!item) return '';
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        style={{ width: 24, height: 24, borderRadius: 999 }}
                        src={pinItems[item.iconName as keyof typeof pinItems]}
                      />
                      <Typography
                        sx={(theme) => ({ color: theme.custom?.text })}
                      >
                        {item.title}
                      </Typography>
                    </Box>
                  );
                }}
              >
                <IconMenuItem disabled value={''}>
                  <Typography
                    sx={(theme) => ({ color: theme.custom?.textLight })}
                  >
                    Select a disposition...
                  </Typography>
                </IconMenuItem>
                {selectedCRM?.dispositions?.map((item) => (
                  <IconMenuItem key={item.id} value={item.id}>
                    <img
                      style={{
                        width: 30,
                        height: 30,
                        aspectRatio: '1/1',
                        borderRadius: 50,
                      }}
                      src={pinItems[item.iconName as keyof typeof pinItems]}
                    />
                    <Typography sx={(theme) => ({ color: theme.custom?.text })}>
                      {item.title}
                    </Typography>
                  </IconMenuItem>
                ))}
              </IconSelect>
            </FormSection>

            <Box
              sx={{ display: 'flex', gap: 2, marginTop: 2, marginBottom: 4 }}
            >
              <SecondaryButton
                startIcon={<GamifyIcon icon="trash" color="inherit" />}
                sx={(theme) => ({
                  color: '#AE1414',
                  borderColor: '#AE1414',
                  '&:hover': {
                    backgroundColor: theme.custom?.isDark
                      ? 'rgba(174, 20, 20, 0.1)'
                      : 'rgba(174, 20, 20, 0.05)',
                  },
                })}
                type="button"
                onClick={onDelete}
                disabled={isSubmitting}
              >
                {stage.isDraft ? 'Cancel' : 'Delete Stage'}
              </SecondaryButton>
              <StyledButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <CircularProgress
                    size={24}
                    sx={(theme) => ({ color: theme.custom?.textCTA })}
                  />
                ) : (
                  <Typography>
                    {' '}
                    {stage.isDraft ? 'Create Stage' : 'Save Changes'}
                  </Typography>
                )}
              </StyledButton>
            </Box>
            <Typography
              sx={(theme) => ({
                fontSize: 12,
                color: theme.custom?.textLight,
                marginLeft: 2,
              })}
            >{`sequence: ${stage.sequence}`}</Typography>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
